import {
  CreateSubscriptionOverloadDocument,
  CreateSubscriptionOverloadMutation,
  GetEnabledModulesDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'

const FACTORIAL_SUITE = 'factorial_suite'
const ALL_FEATURES = [
  'time_off',
  'time_tracking',
  'recruitment',
  'shift_management',
  'engagement',
  'performance',
  'payroll',
  'expenses',
  'space',
  'finance_workspace',
  'trainings',
  'it',
]
const DEFAULT_PLAN = 'core_b-area_a'
const DEFAULT_EXPIRATION_DAYS = 14

type OverloadReason = 'disable_module' | 'enable_module'

interface CreateSubscriptionParams {
  chargebeeId: string
  overloadReason: OverloadReason
}

const useCreateSubscriptionOverload = (companyIds: string[]) => {
  const { data: subscriptionsOverloadData } = useQuery(GetEnabledModulesDocument, {
    variables: { companyIds },
  })

  const [mutate] = useMutation(CreateSubscriptionOverloadDocument, {
    extractorFn: (data: CreateSubscriptionOverloadMutation) =>
      data.companiesMutations.createModuleTrialCompaniesSubscriptionOverload,
  })

  const createSubscription = (params: CreateSubscriptionParams) => {
    const { chargebeeId, overloadReason } = params
    const subscriptionOverload =
      subscriptionsOverloadData?.companies.subscriptionOverloadsConnection.nodes[0]

    const today = new Date()
    today.setDate(today.getDate() + DEFAULT_EXPIRATION_DAYS)

    const planId = subscriptionOverload?.planId || DEFAULT_PLAN
    const expirationDate = subscriptionOverload?.expirationDate || today.toISOString().split('T')[0]
    const addonIds = subscriptionOverload?.addonIds || []
    let addonsUpdated: string[] = []

    if (overloadReason === 'enable_module') {
      // Install a module adding the chargebeeId to the current array
      addonsUpdated = Array.from(new Set([...addonIds, chargebeeId]))
    } else if (overloadReason === 'disable_module') {
      // Uninstall a module adding the chargebeeId to the current array
      addonsUpdated = addonIds.filter((id) => id !== chargebeeId)
    } else if (addonIds.includes(FACTORIAL_SUITE)) {
      // Removing factorial_suite id and changing it for the addonIds list to be able to unistall a specific module
      addonsUpdated = addonIds
        .filter((id) => ALL_FEATURES.some((feat) => id.startsWith(feat)))
        .map((feat) => feat + '_e-area_a')
    } else {
      // Uninstall a module removing the chargebeeId from the current array no matter the suffix (area_a, area_b, etc)
      addonsUpdated = addonIds.filter((id) => !ALL_FEATURES.some((feat) => id.startsWith(feat)))
    }

    return mutate({
      variables: {
        addonIds: addonsUpdated,
        expirationDate: expirationDate || '',
        overloadReason: overloadReason,
        planId: planId,
      },
    })
  }

  return createSubscription
}

export default useCreateSubscriptionOverload
